import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IndianRupee } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { SUBSCRIPTION_PAYMENT_METHOD_OPTIONS } from '@/constants/institute';
import { ApiRequestError } from '@/lib/apiClient';
import { instituteToday } from '@/features/teacher/utils';
import { applyFieldErrors } from '@/lib/forms';
import type { SubscriptionPayment } from '@/types/institute';
import { institutesApi } from '../api/institutes.api';
import {
  defaultRenewalFor,
  licenceYearOf,
  licenceYearOptions,
  subscriptionPaymentResolver,
  subscriptionPaymentSchema,
  type SubscriptionPaymentFormValues,
} from '../schemas/subscriptionPayment.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

export interface RecordSubscriptionPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  institute: { uuid: string; displayName: string };
  /** The page refreshes its ledger and raises the confirmation. */
  onRecorded: (payment: SubscriptionPayment) => void;
}

function blankForm(): SubscriptionPaymentFormValues {
  // The Indian day, not the browser's — the date the money is booked on and
  // the licence year it counts towards are both the platform's calendar.
  const today = instituteToday();
  const periodYear = licenceYearOf(today);
  return {
    periodYear,
    amount: '',
    paidOn: today,
    renewsOn: defaultRenewalFor(periodYear),
    method: 'UPI',
    referenceNo: '',
    note: '',
  };
}

/**
 * Entering a licence payment that has arrived.
 *
 * The renewal date moves the institute's due date forward when it is saved.
 * Whether the payment settles the year — the Paid / Unpaid badge — stays on
 * the edit form, because a part payment does not and only our staff know
 * which this is.
 */
export function RecordSubscriptionPaymentModal({
  isOpen,
  onClose,
  institute,
  onRecorded,
}: RecordSubscriptionPaymentModalProps) {
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<SubscriptionPaymentFormValues>({
    resolver: subscriptionPaymentResolver,
    defaultValues: blankForm(),
  });
  useLiveFormErrors(form);
  const {
    register,
    formState: { errors, isSubmitting },
  } = form;

  // The renewal date follows the chosen year — until somebody types their own,
  // after which it is theirs and a change of year leaves it alone.
  const periodYear = form.watch('periodYear');
  useEffect(() => {
    if (form.getFieldState('renewsOn').isDirty) return;
    form.setValue('renewsOn', defaultRenewalFor(periodYear));
  }, [periodYear, form]);

  // A clean form every time it opens, so last time's amount is never re-sent.
  useEffect(() => {
    if (!isOpen) return;
    form.reset(blankForm());
    setFormError(null);
  }, [isOpen, form]);

  function close() {
    if (isSubmitting) return;
    onClose();
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const payment = await institutesApi.recordSubscriptionPayment(
        institute.uuid,
        subscriptionPaymentSchema.parse(values),
      );
      onRecorded(payment);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(form.setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="md"
      title="Record a payment"
      description={`Money received from ${institute.displayName} for the licence.`}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void onSubmit()}
            isLoading={isSubmitting}
            leftIcon={<IndianRupee className="h-4 w-4" aria-hidden />}
          >
            Record payment
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        {formError && (
          <Alert tone="critical" title="Payment not recorded">
            {formError}
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Amount (₹)"
            inputMode="decimal"
            placeholder="45000"
            leftIcon={<IndianRupee className="h-4 w-4" />}
            error={errors.amount?.message}
            {...register('amount')}
          />
          <Select
            label="For the year"
            options={licenceYearOptions()}
            error={errors.periodYear?.message}
            {...register('periodYear')}
          />
          <DatePicker
            label="Paid on"
            max={instituteToday()}
            error={errors.paidOn?.message}
            {...register('paidOn')}
          />
          <DatePicker
            label="Renews on"
            hint="Moves the institute's due date to this day."
            error={errors.renewsOn?.message}
            {...register('renewsOn')}
          />
        </div>

        <Select
          label="Paid by"
          options={SUBSCRIPTION_PAYMENT_METHOD_OPTIONS}
          error={errors.method?.message}
          {...register('method')}
        />

        <Input
          label="Reference (optional)"
          placeholder="UTR, cheque or transaction number"
          maxLength={100}
          error={errors.referenceNo?.message}
          {...register('referenceNo')}
        />
        <Textarea
          label="Note (optional)"
          placeholder="First of two instalments for 2026-27."
          maxLength={255}
          rows={2}
          error={errors.note?.message}
          {...register('note')}
        />
      </form>
    </Modal>
  );
}
